import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, formatTL, formatDuration } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Plate, COUNTRY_META } from "@/components/Plate";
import { toast } from "sonner";
import { MagnifyingGlass, CarProfile, Warning, CheckCircle } from "@phosphor-icons/react";

export default function DebtInquiry() {
  const [plate, setPlate] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [occupancy, setOccupancy] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/public/occupancy`);
        setOccupancy(data);
      } catch (_e) { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  const query = async (e) => {
    e?.preventDefault();
    const p = plate.trim().toUpperCase();
    if (!p || p.length < 4) return toast.error("Geçerli plaka girin");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/public/debt/${encodeURIComponent(p)}`);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sorgu başarısız");
    } finally { setLoading(false); }
  };

  const hasDebt = result && result.unpaid_total > 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="border-b border-border bg-gradient-to-b from-primary/5 to-transparent">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">
            OTOPARK
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Borç Sorgulama
          </h1>
          <p className="text-muted-foreground max-w-xl">
            Aracınızın otopark borcunu plakanızı yazarak öğrenin. Kayıt gerekmez.
          </p>

          <form onSubmit={query} className="mt-6 flex gap-2 max-w-lg">
            <Input
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              placeholder="34ABC123"
              className="rounded-sm font-plate text-lg uppercase h-12"
              data-testid="debt-plate-input"
              maxLength={12}
              required
            />
            <Button
              type="submit"
              disabled={loading}
              data-testid="debt-search-btn"
              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold h-12 px-6"
            >
              <MagnifyingGlass size={16} /> {loading ? "Aranıyor..." : "Sorgula"}
            </Button>
          </form>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Result */}
        {result && (
          <Card className={`rounded-sm border-2 ${hasDebt ? "border-destructive/40 bg-destructive/5" : "border-green-400 bg-green-50"}`}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                {hasDebt ? (
                  <Warning size={32} weight="fill" className="text-destructive" />
                ) : (
                  <CheckCircle size={32} weight="fill" className="text-green-600" />
                )}
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Plaka</div>
                  <Plate country={result.country?.code} className="text-lg">{result.plate}</Plate>
                </div>
              </div>

              {result.active_session && (
                <div className="border border-border bg-white p-4 rounded-sm">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                    <CarProfile size={12} className="inline mr-1" /> Otoparktasınız
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Süre</div>
                      <div className="text-xl font-mono font-bold">
                        {formatDuration(result.active_session.duration_minutes)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Anlık Ücret</div>
                      <div className="text-xl font-mono font-bold text-primary" data-testid="debt-active-fee">
                        {formatTL(result.active_session.fee)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {result.unpaid_total > 0 && (
                <div className="border border-destructive/30 bg-white p-4 rounded-sm">
                  <div className="text-xs uppercase tracking-widest text-destructive mb-1">
                    Ödenmemiş Oturum(lar)
                  </div>
                  <div className="flex items-baseline justify-between">
                    <div className="text-3xl font-mono font-bold text-destructive" data-testid="debt-unpaid-total">
                      {formatTL(result.unpaid_total)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {result.unpaid_count} oturum
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Otoparktan çıkışta operatöre ödeme yapabilirsiniz.
                  </p>
                </div>
              )}

              {!hasDebt && (
                <p className="text-center text-sm text-green-700 font-semibold" data-testid="debt-no-debt">
                  Bu plakaya ait aktif oturum veya ödenmemiş borç yok.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Live Occupancy */}
        {occupancy && (
          <Card className="rounded-sm border-border">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold tracking-tight">Anlık Doluluk</h3>
                <span className="text-xs uppercase tracking-widest text-muted-foreground">
                  {occupancy.total.occupied}/{occupancy.total.capacity} · %{occupancy.total.percent_full}
                </span>
              </div>
              <div className="space-y-3">
                {occupancy.lots.map((lot) => (
                  <div key={lot.key} data-testid={`occupancy-${lot.key}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-sm font-semibold uppercase tracking-widest">{lot.name}</span>
                      <span className="text-xs font-mono">
                        {lot.occupied}/{lot.capacity} · <b>{lot.free ?? "-"}</b> boş
                      </span>
                    </div>
                    <Progress value={lot.percent_full} className="h-2 rounded-sm" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="text-center text-xs text-muted-foreground py-6">
        Otopark Yönetim · <a href="/login" className="underline underline-offset-4">Yönetim Girişi</a>
      </div>
    </div>
  );
}
